import { Stack, useTheme } from "@mui/material";
import { type Control, useWatch } from "react-hook-form";
import { OverpaidNotice } from "web_myplann/Goals/Components/AmountSections/OverpaidNotice";
import { RemainingRow } from "web_myplann/Goals/Components/AmountSections/RemainingRow";
import { GOAL_AMOUNT_MAX } from "web_myplann/Goals/constants";
import type { GoalFormModel } from "web_myplann/Goals/Forms/useGoalForm";
import { getGoalOverpaidExcess } from "web_myplann/Goals/Helpers/getGoalOverpaidExcess";
import { useGoalCategoryStrings } from "web_myplann/Goals/Hooks/useGoalCategoryStrings";
import type { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";

type Props = {
	control: Control<GoalFormModel>;
	category: GoalCategoryTypeEnum;
};

export const SavingsAmountSection = ({ control, category }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const { amountLabel, amountHint, ownFundsLabel, remainingLabel } =
		useGoalCategoryStrings(category);

	const totalValue = useWatch({ control, name: "totalValue" }) ?? 0;
	const ownFunds = useWatch({ control, name: "ownFunds" }) ?? 0;

	const remaining = Math.max(0, totalValue - ownFunds);
	const overpaidExcess = getGoalOverpaidExcess({ totalValue, ownFunds });
	const showExtras = totalValue > 0;
	const currencySuffix = t(Resources.Goals.Amount.currencySign);

	return (
		<Stack bgcolor={theme.palette.background.paper}>
			{amountHint && <FlatRowHint text={amountHint} />}
			<FlatRowFormInput
				control={control}
				name="totalValue"
				label={amountLabel}
				placeholder={t(Resources.Goals.Form.amountPlaceholder)}
				type="number"
				max={GOAL_AMOUNT_MAX}
				suffix={currencySuffix}
				isLast={!showExtras}
			/>
			{showExtras && (
				<>
					<FlatRowFormInput
						control={control}
						name="ownFunds"
						label={ownFundsLabel}
						placeholder="0"
						type="number"
						max={GOAL_AMOUNT_MAX}
						suffix={currencySuffix}
					/>
					{overpaidExcess > 0 && (
						<OverpaidNotice
							excess={overpaidExcess}
							currencySuffix={currencySuffix}
						/>
					)}
					{remaining > 0 && (
						<RemainingRow label={remainingLabel} amount={remaining} />
					)}
				</>
			)}
		</Stack>
	);
};
